export class EnglishService {

    //#################################################################################
    //
    // This service holds hashmap containing all german strings used in our platform
    // When translating, please keep keys (first argument) same, while
    // adapting the values (seconds argument).
    //
    //#################################################################################

    private lang = new Array();

    constructor() {
        //define strings for missing or excessive number of arguments
        this.populateMissingOrExcessiveArguments();

        //populate all bracket-related errors
        this.populateBracketErrors("color definition");

        //define static string that do not need to be translated
        this.populateStaticStrings();

        //define strings related to login
        this.defineLoginStrings();

        //define key, value pairs (keys are fixed, please adjust values only)
        this.define("yes", "Yes");
        this.define("no", "No");
        this.define("menu", "Menu");
        this.define("saveFile", "Save File");
        this.define("loadFile", "Load File");
        this.define("saveCanvas", "Save Picture");
        this.define("fullscreen", "Enter/Leave fullscreen");
        this.define("newfeatures", "What's new?");
        this.define("Cheatsheet", "Logo overview");
        this.define("background", "Background pictures");

        //language-related strings
        this.define("language", "Choose a language");

        //User Interface: history, input and general UI
        this.define("previousCommands", "Previous commands:");
        this.define("input", "INPUT: ");
        this.define("definitionInInput", "Call commands from the editor without TO and END");
        this.define("timeout", "Execution takes longer than expected - Continue?");
        this.define("errorineditor", "Please fix all errors in the editor first");
        this.define("enterCommand", "Enter command(s) here");
        this.define("errorInInput", "Command not understood.");
        this.define("stopExecution", "Do you want to abort the current execution?");
        this.define("cmdAlreadyFinished", "Command finished already!");
        this.define("hoppla", "Oops...");
        this.define("cancel", "cancel");
        this.define("continue", "continue");

        //boolean values
        this.define("true", "true");
        this.define("false", "false");

        //status update while execution
        this.define("parsingEditor", "Reading command...");
        this.define("executingVisitor", "Executing command...");
        this.define("stoppingVisitor", "Aborting command...");
        this.define("init", "Initializing...")
        this.define("checking", "Checking Editor...");

        //syntax checking: program declaration
        this.define("missingTo", "Missing 'to'");
        this.define("missingEnd", "Missing 'end'");
        this.define("manyTos", "Too many 'to's");
        this.define("manyEnds", "Too many 'end's");
        this.define("maybeTo", "Misspelled 'to'");
        this.define("misspelledEnd", "Misspelled 'end'");
        this.define("noName", "Missing program name");
        this.define("noToNoName", "Missing both 'to' and program name");
        this.define("nameTO", "'to' is not a valid program name");
        this.define("nameEND", "'end' is not a valid program name");
        this.define("nameCMD", "Naming conflict with built-in command");
        this.define("nameFUNC", "Naming conflict with built-in function");
        this.define("nosuchmethod", "There is no program called: ");
        this.define("noEOL", "Start from the next line");
        this.define("noEOLbeforeEND", "'end' cannot be on the same line as other commands");
        this.define("eolAfterTo", "'to' and the program's name must be on the same line");
        this.define("methodNameWhitespace", "Program names cannot contain whitespace");

        //syntax checking: parameters
        this.define("paramsNoColon", "Are you missing ':' before your parameter name?");
        this.define("doubleDeclaration", "Same program name was defined already");
        this.define("eolBeforeParams", "Program name and parameters should be on the same line");
        this.define("paramsOneLine", "All parameters should be on the same line");
        this.define("sameParam", "Same parameter name used several times: ");
        this.define("paramDeclsNotOk", "Erroneous whitespace after ':'");
        this.define("paramDeclNoName", "Missing parameter name after ':'");

        //syntax checking: colors
        this.define("rgbMissingValues", "It takes 3 values to define a color");
        this.define("rgbCommas", "No commas allowed within a color definition");
        this.define("colornameMisspelled", "Misspelled color name. ");
        this.define("noColorWithThisName", "This color doesn't exist: ");

        //syntax checking: blocks and brackets
        this.define("bracketError", "Imbalanced brackets");
        this.define("squareBracketProblem", "Imbalanced square brackets []");
        this.define("roundBracketProblem", "Imbalanced round brackets ()");

        //syntax checking: while
        this.define("whileMissingBlock", "What do you want to be repeated in 'while'?");
        this.define("whileManyBlocks", "Too many [] blocks after 'while'");
        this.define("whileNoArgs", "Missing two parameters after 'while'");

        //syntax checking: repeat
        this.define("repeatMissingExpr", "Missing number after 'repeat'");
        this.define("repeatMissingBlock", "What do you want to repeat?");
        this.define("repeatNoArg", "Incomplete command 'repeat'");
        this.define("repeatNoBrackets", "Missing brackets [] around the commands you want to repeat");
        this.define("repeatManyBlocks", "Too many [] blocks after 'repeat', put all commands in one block only");

        //syntax checking: if-else
        this.define("ifNoBlock", "What do want to execute in 'if'?");
        this.define("ifManyBlocks", "Too many [] parts after 'if'");
        this.define("ifBrackets", "No brackets required around 'if' conditions");
        this.define("ifNoArgs", "Missing something after 'if'");

        //syntax checking: make
        this.define("makeNoVal", "'make' is missing a value as a second parameter");
        this.define("makeNoLit", "'make' is missing \" and a variable name as a first parameter");
        this.define("makeNoArgs", "Missing both parameters after 'make'");
        this.define("derefMake", "Use ':' instead of \" before parameter names");
        this.define("makeReverse", "Reverse parameter order in 'make'");
        this.define("makeColon", "Use \" instead of ':' before parameter name in 'make'");
        this.define("variableNotDeclared", "This variable is not defined: ");
        this.define("makeLitNotOk", "No whitespace allowed after \"");
        this.define("derefNotOk", "Remove whitespace after ':'");

        //syntax checking: general error messages
        this.define("extraneousInput", "There is an error in this program");
        this.define("noViableAlternative", "Did you forget something here?");
        this.define("didYouMean", "Did you mean: ");
        this.define("tooSmall", "Number is too small");
        this.define("specialCharacters", "This character is not allowed: ");
        this.define("whitespace", "Missing whitespace between neighbouring commands");
        this.define("justExpression", "Missing command before parameter");
        this.define("tokenRecognition", "I don't understand: ");
        this.define("tooMuch", "Too much: ");
        this.define("funcMisspelled", " is unknown");

        // Tooltips in header
        this.define("tooltipButtonStop", "Stop the current program.");
        this.define("tooltipButtonDebug", "Execute your program step by step.");
        this.define("tooltipButtonForward", "Take one step backward.");
        this.define("tooltipButtonBackward", "Take one step forward.");
        this.define("tooltipButtonSpeed", "Adjust the execution speed");
        this.define("tooltipButtonLanguage", "Change the language settings");
        this.define("tooltipButtonTurtle", "Change the turtle icon");

        // Debug control buttons
        this.define("buttonDebugToggle", "Step by step");
        this.define("buttonForward", "Forward");
        this.define("buttonBackward", "Backward");

        // Debug watch labels
        this.define("watchPenDown", "Pen down:");
        this.define("watchErasing", "Erase mode:");
        this.define("watchVisible", "Visible:");
        this.define("watchColor", "Pen Color:");
        this.define("watchWidth", "Pen Width:");
        this.define("watchDirection", "Direction:");

        // Run time error exceptions
        this.define("exception_division_by_zero", "Division by 0 is not possible");
        this.define("exception_invalid_argument_asin", "Parameter for arcsin must be between -1 and 1");
        this.define("exception_invalid_argument_acos", "Parameter for arccos must be between -1 and 1");
        this.define("exception_invalid_argument_tan", "Parameter for tan cannot be of the form (0.5 + k) * PI");
        this.define("exception_invalid_argument_mod", "Calculating 'mod n 0' is not possible");
        this.define("exception_invalid_argument_colornumber", "The specified color number does not exist: ");
        this.define("exception_invalid_argument_colorname", "The specified color name does not exist: ");
        this.define("exception_invalid_argument_rgb", "The specified color is not valid: ");
        this.define("exception_sqrt_negative_number", "Square root of negative numbers is not possible");
        this.define("exception_log10_negative_number", "Calculating logarithms of negative numbers is not possible");

        // Catching invalid arguments that might lead to unexpected behavior
        this.define("exception_setpw_negative_number", "Setting a negative pen width is not possible");
        this.define("exception_wait_negative_number", "Waiting a negative amount of time is not possible");
        this.define("exception_circle_negative_number", "Drawing a circle with negative radius is not possible");

        //quickstart: general text
        this.define("tutorialtype", "Choose a tutorial.");
        this.define("doyouneedhelp", "Do you need help?");
        this.define("firststeps", "First steps");
        this.define("definenewcommands", "Defining new commands");
        this.define("debugger", "Step-by-step execution");

        //quickstart: getting started
        this.define("intro1", "In this tutorial you'll learn how to program in XLogoOnline.");
        this.define("intro2", "See the nice drawing? Soon you'll know how to create drawings like these on your own.");
        this.define("intro3", "Programs are composed of commands. FD and BK (used to draw lines) as well as RT and LT (turn left or rigth) are examples of such commands.");
        this.define("intro4", "Let's give it a try. Click inside the area and hit ENTER");
        this.define("intro5", "The turtle executed your program and its results are shown here");
        this.define("intro6", "Please erase the picture using the command CS.");
        this.define("intro7", "CS erases all lines and places the turtle back into the center");
        this.define("intro8", "Progams may even contain multiple commands. Do you know what this program does?");
        this.define("intro9", "All previously issued programs are listed here.");
        this.define("intro10", "Using the ARROW-KEYS " + String.fromCharCode(8679) + " and " + String.fromCharCode(8681) + " you can retrieve old programs without the need to type everything from scratch.");
        this.define("intro11", "What do you think will happen in case of an error?");
        this.define("intro12", "This program is erroneous. Do you spot the error? Find out how the turtle copes with errors.");
        this.define("intro13", "The turtle doesn't understand our program and, instead, issued an error message in orange.");
        this.define("intro14", "Ready. Have fun writing your first program!");

        //quickstart: how to use the editor
        this.define("editor1", "In this tutorial you'll learn how to define and use your own commands.");
        this.define("editor2", "See this program? What do you you think it will do??");
        this.define("editor3", "Okay!? Such a complicated program, only to draw four squares...");
        this.define("editor4", "Isn't there a simpler solution, for example like this?");
        this.define("editor5", "Unfortunately not; the turtle doesn't know command QUADRAT... So let's define it!");
        this.define("editor6", "New commands are defined here. This field is called editor.");
        this.define("editor7", "\'TO\' QUADRAT \'END\' defines a new command named QUADRAT.");
        this.define("editor8", "The same editor window can be used to define multiple commands.");
        this.define("editor9", "Oups! Do you see the red cross on line 5? I indicates an error on this particular line. Any idea? We illegally defined command QUADRAT twicet. (Hint: After this tutorial you can hover over these crosses and receive further diagnostic support)");
        this.define("editor10", "What effect do we want QUADRAT to have? We define our intended behaviour between TO and END");
        this.define("editor11", "Let's see what it does. What do you expect? Give it a try.");
        this.define("editor12", "It's a square, with only one single command! How convenient when drawing bigger pictures with multiple squares...");
        this.define("editor13", "Congratulation! Now you know how to define and invoke your own commands. Have fun!");

        //login service
        this.define("loginButton", "Login");
        this.define("logoutButton", "Logout");
        this.define("registerButton", "Register")
        this.define("loginTitle", "Login");
        this.define("username", "username");
        this.define("password", "password (optional)");
        this.define("successfulLoginTitle", "Login succeeded");
        this.define("successfulLoginText", "You are now logged in as ");
        this.define("failLoginTitle", "Login failed");
        this.define("tryAgain", "Try Again!")
        this.define("registerTitle", "Register New User");
        this.define("repeatPassword", "repeat password");
        this.define("passwordMisatchTitle", "Mismatch");
        this.define("passwordMisatchText", "The two passwords you provided do not match. Do you want to try again?");
        this.define("successfulRegistrationTitle", "Registration succeeded");
        this.define("successfulRegistrationText", "Congratulations! You have been successfully registrated and are now logged in as ");
        this.define("serversuccess", "SUCCESS");
        this.define("unknownuser", "This username is not known to the server");
        this.define("wrongpassword", "You provided the wrong password for the chosen username");
        this.define("usernamealreadytaken", "This username already exists. Choose another one.")
        this.define("toomuchtext", "This document is too big");
        this.define("toomuchtexthint", "Hint: Why not splitting your solution into smaller chunks?");
        this.define("Ihaveread", "I have read and agree to the ");
        this.define("datenschutz", "Privacy Policy");
        this.define("readandaccepted", "");
        this.define("documentexists", "Document exists");
        this.define("overwrite", "Do you want to overwrite it?");
        this.define("writtensuccessfully", "Saved successfully");
    }

    private defineMissingOrIncorrectBrackets(type1, type2, type3, type4, type5, type6, commandname) {
        if (commandname === "NONE") {
            this.define(type1, "Missing right bracket ']'");
            this.define(type2, "Missing left bracket '['");
            this.define(type3, "Missing brackets '[]'");
            this.define(type4, "Incorrect right bracket, use ']' instead");
            this.define(type5, "Incorrect left bracket, use '[' instead");
            this.define(type6, "Incorrect brackets, use '[]' instead");
        } else {
            this.define(type1, "Missing right bracket ']' at '" + commandname + "'");
            this.define(type2, "Missing left bracket '[' at '" + commandname + "'");
            this.define(type3, "Missing brackets '[]' at '" + commandname + "'");
            this.define(type4, "Incorrect right bracket, use ']' at '" + commandname + "'");
            this.define(type5, "Incorrect left bracket, use '[' at '" + commandname + "'");
            this.define(type6, "Incorrect brackets, use '[]' at '" + commandname + "'");
        }
    }

    /*//------------------------------------
    // Strings related to LOGIN
    *///------------------------------------

    private defineLoginStrings() {
        this.define("notingtosave", "Nothing to be saved.");
        this.define("howtosave", "Where would you like to store your document?");
        this.define("save", "Save");
        this.define("locally", "Local");
        this.define("online", "Online");
        this.define("load", "Load programs");
        this.define("noconnection", "Connection failed");
        this.define("yourcode", "Your personal CODE. Write it down to access your programs from anywhere.");
        this.define("filename", "Filename:");
        this.define("login", "Login: ");
        this.define("chapterx", "Chapter X Task Y");
        this.define("loadprogram", "Retrieve program:");
        this.define("chooseprogram", "Choose a program to load");
        this.define("incorrectpassword", "Wrong code");
        this.define("loginsuccess", "Login successfull");
        this.define("youcanloadandstore", "Store and retrieve your programs online "+ "\u2192" +" see menu");
        this.define("code", "Code");
        this.define("iamnew", "I am new...");
        this.define("logoutsuccess", "logout successfull");
    }

    //string for "there are too many arguments after command xy"
    tooManyValuesAfter(keyword, command) {
        if (command === "NONE") {
            this.define(keyword, "Too many parameters after:");
        } else {
            this.define(keyword, "Too many parameters after '" + command + "'");
        }
    }

    //string for "there are too few arguments after command xy"
    tooFewValuesAfter(keyword, command) {
        if (command === "NONE") {
            this.define(keyword, "Missing parameter after: ");
        } else {
            this.define(keyword, "Missing parameter after '" + command + "'");
        }
    }

    /*//-----------------------------------------------------------------------------------------------
    // IMPORTNAT: FROM HERE ON ALL STRINGS ARE STATIC. NO TRANSLATION REQUIRED.
    *///-----------------------------------------------------------------------------------------------

    //populate all bracket-related errors (missing, wrong, left, right or both) in general, colors, print, strings, while
    private populateBracketErrors(stringForColorDefintion) {
        this.defineMissingOrIncorrectBrackets("rgbMissingRightBr", "rgbMissingLeftBr", "rgbMissingBothBr", "rgbWrongRightBr", "rgbWrongLeftBr", "rgbWrongParentheses", stringForColorDefintion);
        this.defineMissingOrIncorrectBrackets("blockMissingRightBr", "blockMissingLeftBr", "blockMissingBothBr", "blockWrongRightBr", "blockWrongLeftBr", "blockWrongBrackets", "NONE");
        this.defineMissingOrIncorrectBrackets("printMissingRightParen", "printMissingLeftParen", "printMissingBothParen", "printWrongRightParen", "printWrongLeftParen", "printWrongParen", "print");
        this.defineMissingOrIncorrectBrackets("quotedstringMissingRightBr", "quotedstringMissingLeftBr", "quotedstringMissingBothBr", "quotedstringWrongRightBr", "quotedstringWrongLeftBr", "quotedstringWrongBrackets", "NONE");
        this.defineMissingOrIncorrectBrackets("whileMissingRightParen", "whileMissingLeftParen", "whileMissingBothParen", "whileWrongRightParen", "whileWrongLeftParen", "whileWrongParen", "print");
    }

    populateMissingOrExcessiveArguments() {
        //too few or too many arguments after built-in commands
        this.tooManyValuesAfter("fdManyArgs", "fd");
        this.tooFewValuesAfter("fdNoArgs", "fd");
        this.tooManyValuesAfter("bkManyArgs", "bk");
        this.tooFewValuesAfter("bkNoArgs", "bk");
        this.tooManyValuesAfter("rtManyArgs", "rt");
        this.tooFewValuesAfter("rtNoArgs", "rt");
        this.tooManyValuesAfter("ltManyArgs", "lt");
        this.tooFewValuesAfter("ltNoArgs", "lt");
        this.tooManyValuesAfter("csManyArgs", "cs");
        this.tooManyValuesAfter("ctManyArgs", "ct");
        this.tooManyValuesAfter("puManyArgs", "pu");
        this.tooManyValuesAfter("pdManyArgs", "pd");
        this.tooManyValuesAfter("peManyArgs", "pe");
        this.tooManyValuesAfter("pptManyArgs", "ppt");
        this.tooManyValuesAfter("htManyArgs", "ht");
        this.tooManyValuesAfter("stManyArgs", "st");
        this.tooManyValuesAfter("setscManyArgs", "setsc");
        this.tooFewValuesAfter("setscNoArg", "setsc");
        this.tooManyValuesAfter("setxManyArgs", "setx");
        this.tooFewValuesAfter("setxNoArg", "setx");
        this.tooManyValuesAfter("setyManyArgs", "sety");
        this.tooFewValuesAfter("setyNoArg", "sety");
        this.tooManyValuesAfter("randomManyArgs", "random");
        this.tooFewValuesAfter("randomNoArg", "random");
        this.tooManyValuesAfter("sinManyArgs", "sin");
        this.tooFewValuesAfter("sinNoArg", "sin");
        this.tooManyValuesAfter("cosManyArgs", "cos");
        this.tooFewValuesAfter("cosNoArg", "cos");
        this.tooManyValuesAfter("tanManyArgs", "tan");
        this.tooFewValuesAfter("tanNoArg", "tan");
        this.tooManyValuesAfter("arcsinManyArgs", "arcsin");
        this.tooFewValuesAfter("arcsinNoArg", "arcsin");
        this.tooManyValuesAfter("arccosManyArgs", "arccos");
        this.tooFewValuesAfter("arccosNoArg", "arccos");
        this.tooManyValuesAfter("arctanManyArgs", "arctan");
        this.tooFewValuesAfter("arctanNoArg", "arctan");
        this.tooManyValuesAfter("absManyArgs", "abs");
        this.tooFewValuesAfter("absNoArg", "abs");
        this.tooManyValuesAfter("sqrtManyArgs", "sqrt");
        this.tooFewValuesAfter("sqrtNoArg", "sqrt");
        this.tooManyValuesAfter("logManyArgs", "log");
        this.tooFewValuesAfter("logNoArg", "log");
        this.tooManyValuesAfter("waitManyArgs", "wait");
        this.tooFewValuesAfter("waitNoArg", "wait");
        this.tooManyValuesAfter("circleManyArgs", "circle");
        this.tooFewValuesAfter("circleNoArg", "circle");
        this.tooManyValuesAfter("setheadingManyArgs", "setheading");
        this.tooFewValuesAfter("setheadingNoArg", "setheading");
        this.tooManyValuesAfter("setpcManyArgs", "setpc");
        this.tooManyValuesAfter("setpwManyArgs", "setpw");
        this.tooFewValuesAfter("setpwNoArg", "setpw");
        this.tooManyValuesAfter("printManyArgs", "print");
        this.tooFewValuesAfter("printNoArg", "print");
        this.tooManyValuesAfter("makeManyVals", "make");
        this.tooManyValuesAfter("homeManyArgs", "home");
        this.tooManyValuesAfter("stopManyArgs", "stop");
        this.tooManyValuesAfter("setxyManyArgs", "setxy");
        this.tooManyValuesAfter("powerManyArgs", "power");
        this.tooManyValuesAfter("washManyArgs", "wash");
        this.tooManyValuesAfter("modManyArgs", "mod");

        //too few or too many arguments after user-defined commands
        this.tooManyValuesAfter("procCallTooManyArgs", "NONE");
        this.tooFewValuesAfter("procCallNotEnoughArgs", "NONE");
    }

    populateStaticStrings() {
        //these strings are static and do not need to be translated ever
        this.define("XLogoOnline", "XLogoOnline");
        this.define("XLogoOnlineColor", "#39456B");
    }

    //populate hashmap with key-value pairs for the given language. Keys will be same in all languages
    define(key: string, value: string) {
        this.lang[key] = value;
    }

    getLang() {
        return this.lang;
    }

}



// WEBPACK FOOTER //
// ./src/app/languages/english.service.ts